/**
 * ChannelPartnerModal — v148.x (Feb 2026)
 *
 * "Become a Channel Partner" — a lightweight lead-capture modal used
 * from the landing page (header, pricing, footer) AND the in-app More
 * menu. Submits to `POST /api/qr-sales/channel-partner/apply` which
 * creates a `qr_sales_employees` row in `pending_approval` state.
 * Admin follows up to assign a QR batch + commission %, then approves.
 *
 * No authentication required — the endpoint is public. The applicant
 * only supplies name + country-code-split phone + location. A short
 * pitch above the form explains the earning model so the applicant
 * knows what they're signing up for before filling out fields.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, MapPin, Phone, User, Loader2, CheckCircle2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳", label: "India" },
  { code: "+1",  flag: "🇺🇸", label: "USA / Canada" },
  { code: "+44", flag: "🇬🇧", label: "UK" },
  { code: "+61", flag: "🇦🇺", label: "Australia" },
  { code: "+971", flag: "🇦🇪", label: "UAE" },
  { code: "+65", flag: "🇸🇬", label: "Singapore" },
  { code: "+60", flag: "🇲🇾", label: "Malaysia" },
];

export default function ChannelPartnerModal({ open, onClose, referralSource = "landing" }) {
  const [form, setForm] = useState({
    name: "",
    country_code: "+91",
    phone: "",
    location: "",
    email: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const digits = String(form.phone || "").replace(/\D/g, "");
    if (!form.name.trim()) { toast.error("Enter your full name"); return; }
    if (digits.length < 6) { toast.error("Enter a valid phone number"); return; }
    if (!form.location.trim()) { toast.error("Enter your city or area"); return; }
    setBusy(true);
    try {
      await api.post("/qr-sales/channel-partner/apply", {
        name: form.name.trim(),
        country_code: form.country_code,
        phone: digits,
        location: form.location.trim(),
        email: form.email.trim() || undefined,
        referral_source: referralSource,
      });
      setDone(true);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ name: "", country_code: "+91", phone: "", location: "", email: "" });
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      data-testid="channel-partner-modal"
    >
      <div
        className="w-full sm:max-w-md bg-white border-4 border-ink shadow-brutal rounded-t-lg sm:rounded-none overflow-y-auto"
        style={{ maxHeight: "100dvh" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b-4 border-ink bg-coral text-white flex items-start gap-2 sticky top-0 z-10">
          <TrendingUp className="w-5 h-5 mt-0.5" strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-lg leading-tight">Become a Channel Partner</h3>
            <p className="text-xs opacity-90 mt-0.5">Earn recurring income by selling Linkola loyalty QRs to nearby shops.</p>
          </div>
          <button type="button" onClick={close} data-testid="channel-partner-close" aria-label="Close" className="border-2 border-white p-0.5 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="p-6 space-y-3 text-center" data-testid="channel-partner-done">
            <div className="inline-flex items-center justify-center w-14 h-14 border-4 border-ink bg-mint">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h4 className="font-display font-black text-xl">Application received</h4>
            <p className="text-sm text-ink/70">
              Our team will call <b>{form.phone ? `${form.country_code} ${form.phone}` : "you"}</b> within 24 hours to onboard you as a Linkola Channel Partner.
            </p>
            <ul className="text-xs text-ink/60 space-y-1 max-w-xs mx-auto text-left border-2 border-ink bg-cream p-3">
              <li>• We assign a QR batch to your territory</li>
              <li>• Agree on a commission % per sale</li>
              <li>• You start selling and earn on every renewal</li>
            </ul>
            <button type="button" onClick={close} className="border-2 border-ink bg-ink text-white px-4 py-2 text-sm font-bold shadow-brutalSm">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-4 space-y-3" data-testid="channel-partner-form">
            {/* Pitch */}
            <div className="border-2 border-ink bg-sun px-3 py-2 space-y-1">
              <p className="text-[10px] font-black uppercase tracking-widest">What you sell</p>
              <p className="text-xs leading-relaxed">
                A <b>loyalty QR system</b> for local shops: customers scan, earn coins on every purchase, come back more often. You earn a % on every QR sold + on every renewal.
              </p>
            </div>

            {/* Name */}
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                <User className="w-3 h-3" /> Full name
              </span>
              <input
                type="text"
                autoFocus
                required
                maxLength={80}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="channel-partner-name"
                className="w-full border-2 border-ink px-3 py-2 text-sm"
                placeholder="Your name"
              />
            </label>

            {/* Phone with country code */}
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone (WhatsApp preferred)
              </span>
              <div className="flex gap-2 items-stretch">
                <select
                  value={form.country_code}
                  onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                  data-testid="channel-partner-country-code"
                  className="w-[110px] border-2 border-ink px-2 py-2 text-sm font-mono bg-white"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  required
                  pattern="[0-9]{6,15}"
                  maxLength={15}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                  data-testid="channel-partner-phone"
                  className="flex-1 border-2 border-ink px-3 py-2 text-sm font-mono"
                  placeholder="10-digit number"
                />
              </div>
            </div>

            {/* Location */}
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                <MapPin className="w-3 h-3" /> City / area
              </span>
              <input
                type="text"
                required
                maxLength={120}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="channel-partner-location"
                className="w-full border-2 border-ink px-3 py-2 text-sm"
                placeholder="e.g. HSR Layout, Bengaluru"
              />
            </label>

            {/* Optional email */}
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-ink/60">Email (optional)</span>
              <input
                type="email"
                maxLength={120}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="channel-partner-email"
                className="w-full border-2 border-ink px-3 py-2 text-sm"
                placeholder="you@example.com"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              data-testid="channel-partner-submit"
              className="w-full border-2 border-ink bg-coral text-white px-4 py-2.5 text-sm font-black shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
              ) : (
                <>Apply now — we'll call you</>
              )}
            </button>
            <p className="text-[10px] text-ink/50 text-center">
              By applying, you agree to be contacted about the Linkola Channel Partner programme.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
